/**
 * Coach Health Score — single 0-100 metric of trading-process health.
 *
 * Composite of five factors that, together, capture how well-defined and
 * well-followed the trader's current process is. Designed for at-a-glance
 * "where do I stand right now?" — like a credit score for trading discipline.
 *
 * Factor breakdown (max 100):
 *   - Edges presence       (0-25) — # of active edges in the profile (more = healthier process)
 *   - Leak burden          (0-25) — # of active leaks (inverse: fewer leaks = higher score)
 *   - Chronic-leak penalty (0-20) — leaks running 3+ snapshots without resolution
 *   - Profile freshness    (0-15) — recency of last profile rebuild (stale = lower)
 *   - Resolution credit    (0-15) — leaks resolved in the most-recent snapshot diff
 *
 * Letter grade derived from score: A ≥ 85, B ≥ 70, C ≥ 55, D ≥ 40, F < 40.
 *
 * Trend: compares current score to the score we'd get if the most-recent
 * history snapshot were "current" — gives an up/down/flat arrow.
 *
 * Pure: takes profile + history, returns the score object. No I/O, no
 * randomness.
 */

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

// Cap counts at sane upper bounds so a trader with 50 edges doesn't wash
// out the leak factor's contribution. Both factors are saturating curves.
const EDGES_FOR_FULL_SCORE = 5;     // 5+ active edges = full 25 pts
const LEAKS_FOR_ZERO_SCORE = 8;     // 8+ active leaks = zero on the leak factor

// Persistence threshold — leaks running this many snapshots count as chronic
const CHRONIC_RUN_FLOOR = 3;
const CHRONIC_FOR_FULL_PENALTY = 5;  // 5+ chronic leaks = full 20pt penalty

// Profile freshness penalties grow linearly with age (in days).
const FRESHNESS_FULL_DAYS = 1;       // ≤1 day = full 15 pts
const FRESHNESS_ZERO_DAYS = 14;      // ≥14 days = 0 pts

// Resolution credit — count of leaks resolved this snapshot
const RESOLUTIONS_FOR_FULL_SCORE = 3;  // 3+ resolved this snapshot = full 15

// Letter-grade cutoffs
export const GRADE_CUTOFFS = Object.freeze({
  A: 85, B: 70, C: 55, D: 40,  // anything below D = F
});

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
const round = (n) => Math.round(n);

/**
 * Convert a 0..1 saturating value into a 0..max score.
 */
const saturating = (value, max) => clamp(value, 0, 1) * max;

/**
 * Letter grade for a score.
 */
export const gradeForScore = (score) => {
  if (!Number.isFinite(score)) return 'F';
  if (score >= GRADE_CUTOFFS.A) return 'A';
  if (score >= GRADE_CUTOFFS.B) return 'B';
  if (score >= GRADE_CUTOFFS.C) return 'C';
  if (score >= GRADE_CUTOFFS.D) return 'D';
  return 'F';
};

/**
 * Compute the five factor breakdowns + composite score for a single
 * profile snapshot. Used by both the current-score path and the
 * trend-comparison path (with reconstructed prior data).
 *
 * @param {Object} args
 * @param {number} args.edgeCount         active edges in the profile
 * @param {number} args.leakCount         active leaks in the profile
 * @param {number} args.chronicLeakCount  leaks that have been firing for ≥CHRONIC_RUN_FLOOR snapshots
 * @param {number} args.daysSinceBuild    days since the profile was last rebuilt (≥0)
 * @param {number} args.recentResolutions leaks resolved in the most-recent snapshot diff
 * @returns {{ score: number, factors: Array<{id,label,value,score,max,direction}> }}
 */
export const composeScore = ({
  edgeCount = 0,
  leakCount = 0,
  chronicLeakCount = 0,
  daysSinceBuild = 0,
  recentResolutions = 0,
} = {}) => {
  // Edges presence — saturating to EDGES_FOR_FULL_SCORE
  const edgesScore = saturating(edgeCount / EDGES_FOR_FULL_SCORE, 25);

  // Leak burden — inverted. 0 leaks = 25 pts, LEAKS_FOR_ZERO_SCORE+ leaks = 0
  const leakBurden = clamp(leakCount / LEAKS_FOR_ZERO_SCORE, 0, 1);
  const leaksScore = (1 - leakBurden) * 25;

  // Chronic-leak penalty — inverse, max 20 pts (so no chronic leaks = full 20)
  const chronicShare = clamp(chronicLeakCount / CHRONIC_FOR_FULL_PENALTY, 0, 1);
  const chronicScore = (1 - chronicShare) * 20;

  // Freshness — linear taper from FULL_DAYS to ZERO_DAYS
  const freshnessAge = clamp(daysSinceBuild, FRESHNESS_FULL_DAYS, FRESHNESS_ZERO_DAYS);
  const freshnessFraction = 1 - (freshnessAge - FRESHNESS_FULL_DAYS) / (FRESHNESS_ZERO_DAYS - FRESHNESS_FULL_DAYS);
  const freshnessScore = clamp(freshnessFraction, 0, 1) * 15;

  // Resolution credit — saturating to RESOLUTIONS_FOR_FULL_SCORE
  const resolutionScore = saturating(recentResolutions / RESOLUTIONS_FOR_FULL_SCORE, 15);

  const total = edgesScore + leaksScore + chronicScore + freshnessScore + resolutionScore;

  return {
    score: round(total),
    factors: [
      {
        id: 'edges', label: 'Active edges', value: edgeCount,
        score: round(edgesScore), max: 25, direction: 'up',
      },
      {
        id: 'leaks', label: 'Active leaks', value: leakCount,
        score: round(leaksScore), max: 25, direction: 'down',
      },
      {
        id: 'chronic', label: 'Chronic leaks (3+ runs)', value: chronicLeakCount,
        score: round(chronicScore), max: 20, direction: 'down',
      },
      {
        id: 'freshness', label: 'Profile freshness',
        value: daysSinceBuild != null ? `${round(daysSinceBuild)}d ago` : 'never',
        score: round(freshnessScore), max: 15, direction: 'down',
      },
      {
        id: 'resolution', label: 'Recent resolutions', value: recentResolutions,
        score: round(resolutionScore), max: 15, direction: 'up',
      },
    ],
  };
};

/**
 * Extract the inputs composeScore needs from a current CoachProfile.
 */
const profileToScoreInputs = (profile, anchorMs = Date.now()) => {
  if (!profile) {
    return {
      edgeCount: 0, leakCount: 0, chronicLeakCount: 0,
      daysSinceBuild: FRESHNESS_ZERO_DAYS, recentResolutions: 0,
    };
  }
  const edgeCount = (profile.edges || []).length;
  const leakCount = (profile.leaks || []).length;
  // Chronic = leaks with occurrences ≥ CHRONIC_RUN_FLOOR. occurrences is
  // carried forward across rebuilds by buildProfile so it represents how
  // many snapshots the signature has been present in.
  const chronicLeakCount = (profile.leaks || []).filter(l => (l.occurrences || 1) >= CHRONIC_RUN_FLOOR).length;
  const recentResolutions = (profile.resolvedLeaks || []).length;
  let daysSinceBuild = FRESHNESS_ZERO_DAYS;
  if (profile.generatedAt) {
    const t = Date.parse(profile.generatedAt);
    if (Number.isFinite(t)) daysSinceBuild = Math.max(0, (anchorMs - t) / DAY_MS);
  }
  return { edgeCount, leakCount, chronicLeakCount, daysSinceBuild, recentResolutions };
};

/**
 * Reconstruct prior-period score inputs from the most-recent history
 * snapshot. The compact snapshot stores only signature lists + counts,
 * not full profile entries — so chronic-leak count and recent-resolutions
 * can't be exactly recomputed. We approximate:
 *
 *   - edgeCount/leakCount: from snapshot signature lists (exact)
 *   - chronicLeakCount: 0 (we don't track per-signature occurrence in the
 *     compact snapshot; treating prior-period as having no chronics is
 *     conservative — trend will UNDERSTATE improvements)
 *   - recentResolutions: snapshot.resolvedCount (exact)
 *   - daysSinceBuild: 0 (the snapshot itself was "current" at the time)
 */
const snapshotToPriorScoreInputs = (snapshot) => {
  if (!snapshot) return null;
  return {
    edgeCount: (snapshot.edgeSignatures || []).length,
    leakCount: (snapshot.leakSignatures || []).length,
    chronicLeakCount: 0,
    daysSinceBuild: 0,  // snapshot was "now" at the time
    recentResolutions: snapshot.resolvedCount || 0,
  };
};

/**
 * Public API: compute the current health score + trend.
 *
 * @param {Object} args
 * @param {Object} args.profile         current CoachProfile (or null)
 * @param {Array}  args.history         compact history snapshots (newest-first)
 * @param {number} [args.anchor]        epoch ms; defaults to now (for tests)
 * @returns {{
 *   score: number, grade: string,
 *   factors: Array,
 *   trend: 'up' | 'down' | 'flat' | 'first',
 *   delta: number | null,
 *   priorScore: number | null,
 * }}
 */
export const computeHealthScore = ({ profile = null, history = [], anchor } = {}) => {
  const anchorMs = Number.isFinite(anchor) ? anchor : Date.now();
  const current = composeScore(profileToScoreInputs(profile, anchorMs));
  const priorSnap = Array.isArray(history) && history.length > 0 ? history[0] : null;
  let priorScore = null;
  let trend = 'first';
  let delta = null;
  if (priorSnap) {
    const priorInputs = snapshotToPriorScoreInputs(priorSnap);
    if (priorInputs) {
      const prior = composeScore(priorInputs);
      priorScore = prior.score;
      delta = current.score - prior.score;
      if (delta > 1) trend = 'up';
      else if (delta < -1) trend = 'down';
      else trend = 'flat';
    }
  }
  return {
    score: current.score,
    grade: gradeForScore(current.score),
    factors: current.factors,
    trend,
    delta,
    priorScore,
  };
};

// Test-friendly exports for the helpers + threshold constants. (composeScore
// + gradeForScore + GRADE_CUTOFFS are already exported inline above; re-
// exporting them here would be a SyntaxError.)
export {
  profileToScoreInputs,
  snapshotToPriorScoreInputs,
  CHRONIC_RUN_FLOOR,
  EDGES_FOR_FULL_SCORE,
  LEAKS_FOR_ZERO_SCORE,
  CHRONIC_FOR_FULL_PENALTY,
  FRESHNESS_FULL_DAYS,
  FRESHNESS_ZERO_DAYS,
  RESOLUTIONS_FOR_FULL_SCORE,
};
